import { FC } from "react";
import ReactTooltip from "react-tooltip";

import { combineClassNames } from "helpers/shared/classNames";

import { IMainMenuItem } from "./types";
import { useMainMenu } from "./useMainMenu";
import {
  CollapseBtn,
  CollapseBtnIcon,
  IconContainer,
  LabelContainer,
  List,
  LogoContainer,
  MBMainContainer,
  MBTopContainer,
  MenuBaseLink,
  MenuBlock,
  MenuLink,
  StyledLogo,
  StyledLogoFullAlt,
} from "./styles";

export interface Props {
  needRenderMenuForResponsive?: boolean;
}

const MainMenu: FC<Props> = ({ needRenderMenuForResponsive }) => {
  const { metadata, actions, classNames } = useMainMenu();
  const { classNameByRole, collapsedClassName, animationClassName } = classNames;

  const menuBlockClassName = combineClassNames(classNameByRole, collapsedClassName);
  const menuItemContainerClassName = combineClassNames(classNameByRole, collapsedClassName);
  const menuCollapsedContentWithAnimationClassName = combineClassNames(
    collapsedClassName,
    animationClassName,
  );

  const menu: IMainMenuItem[] = metadata.getMenu();

  const renderMenuLink = (item: IMainMenuItem) => (
    <>
      <IconContainer className={collapsedClassName}>{item.icon}</IconContainer>
      <LabelContainer className={collapsedClassName}>{item.label}</LabelContainer>

      <ReactTooltip
        id={item.id}
        place="right"
        type="dark"
        effect="solid"
        className={`tooltip ${collapsedClassName}`}
      />
    </>
  );

  const renderMenuLinkContainer = (item: IMainMenuItem) => {
    const commonProps = {
      className: menuItemContainerClassName,
      key: item.id,
      ["data-for"]: item.id,
      ["data-testid"]: `main-menu-item-${item.id}`,
      ["data-tip"]: item.label,
    };

    if (item.href)
      return (
        <MenuBaseLink href={item.href} target="_blank" {...commonProps}>
          {renderMenuLink(item)}
        </MenuBaseLink>
      );
    if (item.url)
      return (
        <MenuLink to={item.url} {...commonProps}>
          {renderMenuLink(item)}
        </MenuLink>
      );
  };

  return (
    <MenuBlock
      className={menuBlockClassName}
      needRenderMenuForResponsive={!!needRenderMenuForResponsive}
    >
      <MBTopContainer className={collapsedClassName}>
        {!needRenderMenuForResponsive && (
          <LogoContainer className={collapsedClassName}>
            <StyledLogo className={menuCollapsedContentWithAnimationClassName} />
            <StyledLogoFullAlt className={menuCollapsedContentWithAnimationClassName} />
          </LogoContainer>
        )}
        {!needRenderMenuForResponsive && (
          <CollapseBtn
            className={menuCollapsedContentWithAnimationClassName}
            onClick={actions.handleCollapse}
            data-testid="main-menu-collapse-button"
          >
            <CollapseBtnIcon className={collapsedClassName} />
          </CollapseBtn>
        )}
      </MBTopContainer>

      <MBMainContainer>
        <List>{menu.map((item: IMainMenuItem) => renderMenuLinkContainer(item))}</List>
      </MBMainContainer>
    </MenuBlock>
  );
};

export default MainMenu;
